import { supabase } from './supabaseClient';

// Vimeo operations via Supabase Edge Function
const VIMEO_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vimeo_transfer`;

/**
 * Upload video to Vimeo from URL (pull upload) via Edge Function
 * This is the fastest method for videos already hosted somewhere (like Cloudinary)
 * 
 * @param {string} videoUrl - URL of video to upload (Cloudinary, direct URL, etc.)
 * @param {object} metadata - Video metadata (title, description, etc.)
 * @returns {object} Vimeo video data with player URLs
 */
export const uploadVideoToVimeo = async (videoUrl, metadata = {}) => {
  try {
    console.log('🎬 Starting Vimeo upload from URL:', videoUrl);

    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(VIMEO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
      },
      body: JSON.stringify({
        action: 'upload',
        videoUrl,
        metadata: {
          title: metadata.title || 'VibrantCast Pulse Video',
          description: metadata.description || ''
        }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Vimeo upload failed');
    }

    const result = await response.json();
    console.log('✅ Vimeo upload complete:', result.id);

    return result;

  } catch (error) {
    console.error('❌ Vimeo upload error:', error.message);
    throw new Error(`Vimeo upload failed: ${error.message}`);
  }
};

/**
 * Update video privacy to public after approval
 * 
 * @param {string} videoId - Vimeo video ID
 */
export const makeVimeoVideoPublic = async (videoId) => {
  try {
    console.log('🔓 Making Vimeo video public:', videoId);

    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(VIMEO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
      },
      body: JSON.stringify({
        action: 'updatePrivacy',
        videoId,
        privacy: 'anybody'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to make video public');
    }

    console.log('✅ Video is now public');

  } catch (error) {
    console.error('❌ Error making video public:', error.message);
    throw new Error(`Failed to make video public: ${error.message}`);
  }
};

/**
 * Update video privacy to unlisted (private)
 * 
 * @param {string} videoId - Vimeo video ID
 */
export const makeVimeoVideoUnlisted = async (videoId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(VIMEO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
      },
      body: JSON.stringify({
        action: 'updatePrivacy',
        videoId,
        privacy: 'unlisted'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to set video unlisted');
    }

    console.log('🔒 Video set to unlisted');

  } catch (error) {
    console.error('Error setting video to unlisted:', error.message);
    throw error;
  }
};

/**
 * Delete video from Vimeo
 * 
 * @param {string} videoId - Vimeo video ID
 */
export const deleteVimeoVideo = async (videoId) => {
  try {
    console.log('🗑️ Deleting Vimeo video:', videoId);

    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(VIMEO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
      },
      body: JSON.stringify({
        action: 'delete',
        videoId
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete video');
    }

    console.log('✅ Video deleted from Vimeo');

  } catch (error) {
    console.error('❌ Error deleting video:', error.message);
    throw new Error(`Failed to delete video: ${error.message}`);
  }
};

/**
 * Get Vimeo video details
 * 
 * @param {string} videoId - Vimeo video ID
 * @returns {object} Video data
 */
export const getVimeoVideoDetails = async (videoId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(VIMEO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
      },
      body: JSON.stringify({
        action: 'getDetails',
        videoId
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get video details');
    }

    return await response.json();

  } catch (error) {
    console.error('Error fetching video details:', error.message);
    throw error;
  }
};

/**
 * Extract Vimeo video ID from various URL formats
 * 
 * @param {string} url - Vimeo URL
 * @returns {string|null} Video ID or null if not found
 */
export const extractVimeoId = (url) => {
  if (!url) return null;

  // Handle different Vimeo URL formats:
  // https://vimeo.com/123456789
  // https://player.vimeo.com/video/123456789
  // https://player.vimeo.com/progressive_redirect/playback/123456789/...
  
  const patterns = [
    /vimeo\.com\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/,
    /player\.vimeo\.com\/progressive_redirect\/playback\/(\d+)/,
    /\/videos\/(\d+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
};

/**
 * Check if URL is a Vimeo URL
 * 
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isVimeoUrl = (url) => {
  if (!url) return false;
  return url.includes('vimeo.com') || url.includes('vimeocdn.com');
};

/**
 * Check if URL is a Cloudinary video URL
 * 
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isCloudinaryVideo = (url) => {
  if (!url) return false;
  return url.includes('cloudinary.com') && (url.includes('/video/') || url.match(/\.(mp4|mov|avi|webm)$/i));
};

/**
 * Get Vimeo account info (for debugging/verification)
 * Note: This might not work via edge function due to /me endpoint limitations
 * 
 * @returns {object} Account information
 */
export const getVimeoAccountInfo = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(VIMEO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`
      },
      body: JSON.stringify({
        action: 'getAccount'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get account info');
    }

    return await response.json();

  } catch (error) {
    console.error('Error fetching account info:', error.message);
    throw error;
  }
};
